// Libs
import axios from 'axios';
import downloadPrivatePhoto from '../privatePhotoDownload';

// interface RequestProps {
// 	url: string;
// 	params?: Object;
// 	data?: Object;
// 	withAuth?: boolean;
// }

// interface FetchProps extends RequestProps {
// 	method: Method;
// 	headers?: Object;
// }

const apiService = axios.create({
	baseURL: process.env.REACT_APP_API_URL,
});

const unsafeCustomHeaderNames = new Set([
	'authorization',
	'__proto__',
	'prototype',
	'constructor',
]);

const run = ({
	url,
	params, // the URL parameters to be sent with the request
	data, // the data to be sent as the request body, only applicable for request methods 'PUT', 'POST', and 'PATCH'
	headers,
	withAuth = true,
	method,
	controlSignal,
	preserveErrorEnvelope = false,
}) => {
	return new Promise(async (resolve, reject) => {
		const authHeader = withAuth
			? {
					Authorization: `Bearer ${localStorage.getItem('synapse_token')}`,
				}
			: undefined;
		const customHeaders =
			headers && typeof headers === 'object' && !Array.isArray(headers)
				? Object.entries(headers).reduce((result, [key, value]) => {
						if (!unsafeCustomHeaderNames.has(String(key).toLowerCase())) {
							result[key] = value;
						}
						return result;
					}, Object.create(null))
				: {};
		const requestHeaders = withAuth
			? { ...customHeaders, ...authHeader }
			: customHeaders;
		const axiosConfig = {
			url,
			params,
			data,
			method,
			headers:
				Object.keys(requestHeaders).length > 0 ? requestHeaders : undefined,
			...(controlSignal ? { signal: controlSignal } : {}),
		};

		return apiService(axiosConfig)
			.then(response => resolve(response.data))
			.catch(err => {
				const resData = err?.response?.data || {};
				const resStatus = err?.response?.status || 0;
				const retryAfter = err?.response?.headers?.['retry-after'];
				// Show multiple errors?
				if (
					!preserveErrorEnvelope &&
					(resData.showError || resStatus >= 500) &&
					Array.isArray(resData.errors) &&
					resData.errors.length > 0
				) {
					return reject(resData.errors);
				}
				return reject(
					resStatus > 0 &&
						resData &&
						typeof resData === 'object' &&
						!Array.isArray(resData)
						? {
								...resData,
								httpStatus: resStatus,
								...(retryAfter ? { retryAfter } : {}),
							}
						: resData,
				);
			});
	});
};

export default class ApiService {
	static async getImageBlob({ url, controlSignal, params, data } = {}) {
		if (typeof url !== 'string' || !/^\/collector_values\/photos\/[1-9][0-9]*\/(original|70|150|400)$/.test(url) ||
			!Number.isSafeInteger(Number(url.split('/')[3])) ||
			params !== undefined || data !== undefined) throw new Error('PRIVATE_PHOTO_UNAVAILABLE');
		const source = axios.CancelToken.source();
		const abort = () => source.cancel('PHOTO_DOWNLOAD_CANCELLED');
		if (controlSignal && controlSignal.aborted) throw new Error('PRIVATE_PHOTO_UNAVAILABLE');
		if (controlSignal) controlSignal.addEventListener('abort', abort);
		try {
			const token = localStorage.getItem('synapse_token');
			return await downloadPrivatePhoto({ path: url, signal: controlSignal,
			  isCurrent: () => localStorage.getItem('synapse_token') === token,
			  requestTicket: async () => {
			    const response = await apiService({ url: url + '/url', method: 'get', responseType: 'json',
			      timeout: 10000, cancelToken: source.token, headers: { Authorization: `Bearer ${token}` } });
			    return response.data;
			  } });
		} catch (_) { throw new Error('PRIVATE_PHOTO_UNAVAILABLE'); }
		finally { if (controlSignal) controlSignal.removeEventListener('abort', abort); }
	}

	static async getBlob({ url, controlSignal, params, data } = {}) {
		const ids = typeof url === 'string' && (
			/^\/orders\/reports\/([1-9][0-9]*)\/([1-9][0-9]*)\/pdf$/.exec(url) ||
			/^\/payments\/pdf\/([1-9][0-9]*)\/([1-9][0-9]*)$/.exec(url) ||
			/^\/paycuts\/pdf\/([1-9][0-9]*)\/([1-9][0-9]*)\/(?:include|exclude)-payroll-users$/.exec(url));
		if (!ids || !ids.slice(1).every(id => Number.isSafeInteger(Number(id))) ||
			params !== undefined || data !== undefined) return Promise.reject({});
		const source = axios.CancelToken.source();
		const abort = () => source.cancel('PDF_DOWNLOAD_CANCELLED');
		if (controlSignal?.aborted) return Promise.reject({});
		controlSignal?.addEventListener('abort', abort);
		try {
			const response = await apiService({ url, method: 'get', responseType: 'blob', cancelToken: source.token,
				headers: { Authorization: `Bearer ${localStorage.getItem('synapse_token')}` },
				...(controlSignal ? { signal: controlSignal } : {}),
			});
			return response.data;
		} catch (_) { return Promise.reject({}); }
		finally { controlSignal?.removeEventListener('abort', abort); }
	}

	static async get(props) {
		return run({ ...props, method: 'get' });
	}

	static async post(props) {
		return run({ ...props, method: 'post' });
	}

	static async put(props) {
		return run({ ...props, method: 'put' });
	}

	static async patch(props) {
		return run({ ...props, method: 'patch' });
	}

	static async delete(props) {
		return run({ ...props, method: 'delete' });
	}
}
